import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { APP_PATHS, INSTALLATION_DETAIL_TABS, XEMEX_OID } from '@core/constants';
import { getUserFeaturePermissions } from '@core/models';
import { TabViewModel } from '@core/models/tab-view.model';
import { RecentlyVisitedService } from '@core/services';
import {
  PageBodyComponent,
  PageHeaderBadge,
  PageHeaderComponent,
  TabBarComponent,
} from '@custom-components';
import { BreadcrumbItem } from '@custom-components/general/breadcrumb/breadcrumb.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthStore } from '@root/app/store/auth.store';
import { DeviceEnergyflowStore } from '@root/app/store/device-energyflow.store';
import { InstallationTelemetryStore } from '@root/app/store/installation-telemetry.store';
import { InstallationStore } from '@root/app/store/installation.store';
import { UserStore } from '@root/app/store/user.store';
import { InstallationActivityTabComponent } from '../../components/installation-activity-tab/installation-activity-tab.component';
import { InstallationAlarmsTabComponent } from '../../components/installation-alarms-tab/installation-alarms-tab.component';
import { InstallationDataSourcesTabComponent } from '../../components/installation-data-sources-tab/installation-data-sources-tab.component';
import { InstallationGeneralTabComponent } from '../../components/installation-general-tab/installation-general-tab.component';
import { InstallationInsightsTabComponent } from '../../components/installation-insights-tab/installation-insights-tab.component';
import { InstallationSettingsTabComponent } from '../../components/installation-settings-tab/installation-settings-tab.component';
import { fedDevices } from '../../fed-devices';
import { installationAddress } from '../../installation-labels';

@Component({
  selector: 'xemex-installation-detail',
  imports: [
    InstallationActivityTabComponent,
    InstallationAlarmsTabComponent,
    InstallationDataSourcesTabComponent,
    InstallationGeneralTabComponent,
    InstallationInsightsTabComponent,
    InstallationSettingsTabComponent,
    PageBodyComponent,
    PageHeaderComponent,
    TabBarComponent,
    TranslateModule,
  ],
  // The stores belong to this page, so a second installation starts from a clean slate and
  // the insights tab owns the one telemetry response and device reads.
  providers: [InstallationStore, InstallationTelemetryStore, DeviceEnergyflowStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './installation-detail.component.html',
})
export class InstallationDetailComponent {
  readonly installationStore = inject(InstallationStore);
  private readonly telemetryStore = inject(InstallationTelemetryStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly authStore = inject(AuthStore);
  private readonly userStore = inject(UserStore);
  private readonly recentlyVisited = inject(RecentlyVisitedService);

  readonly installationOid: string = this.route.snapshot.params['installationOid'];
  readonly selectedTab = signal<string>(this.route.snapshot.params['tab'] ?? 'general');
  private telemetryRequested = false;
  private recorded = false;

  readonly permissions = computed(() =>
    getUserFeaturePermissions(this.userStore.user(), this.authStore.permissions(), XEMEX_OID),
  );

  // Re-indexed after filtering, because the tab bar selects by index.
  readonly tabs = computed<TabViewModel[]>(() =>
    INSTALLATION_DETAIL_TABS.filter(
      tab => !tab.permission || this.permissions().includes(tab.permission),
    ).map((tab, index) => ({ index, title: tab.title, route: tab.route })),
  );

  // A tab the user may not open is never rendered, so nothing in it can make a request before the
  // redirect below runs.
  readonly shownTab = computed(() => {
    const tab = this.selectedTab();
    return this.tabs().some(candidate => candidate.route === tab) ? tab : null;
  });

  readonly installation = computed(() => this.installationStore.detail());
  readonly feedingDevices = computed(() => {
    const installation = this.installation();
    return installation ? fedDevices(installation) : [];
  });
  readonly address = computed(() => installationAddress(this.installation()));

  // Only the first read shows the header as loading. A tab reads the installation again after
  // every write, and the header must stay as it is on every tab while that happens.
  readonly headerLoading = computed(
    () => this.installationStore.isLoadingDetail() && !this.installation(),
  );

  readonly breadcrumbs = computed<BreadcrumbItem[]>(() => [
    { label: this.translate.instant('sidebarMenu.home'), routerLink: '/' },
    {
      label: this.translate.instant('sidebarMenu.installations'),
      routerLink: '/' + APP_PATHS.Installations,
    },
    { label: this.installation()?.name ?? '—' },
  ]);

  readonly badges = computed<PageHeaderBadge[]>(() => {
    const installation = this.installation();
    if (!installation) return [];
    const badges: PageHeaderBadge[] = [
      {
        label: this.translate.instant(`installations.status.${installation.status}`),
        status: installation.status === 'Active' ? 'success' : 'inactive',
      },
    ];
    // A retired installation is read-only, so whether it was commissioned means nothing.
    if (installation.status === 'Active' && installation.commissioningComplete !== null) {
      const complete = installation.commissioningComplete;
      badges.push({
        label: this.translate.instant(
          complete
            ? 'installations.commissioning.badge.complete'
            : 'installations.commissioning.badge.incomplete',
        ),
        status: complete ? 'success' : 'warning',
      });
    }
    return badges;
  });

  constructor() {
    // A link from one tab into another changes only the route's tab, and the page stays.
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe(params => {
      const tab = params.get('tab');
      if (tab) this.selectedTab.set(tab);
    });

    this.installationStore.loadDetail(this.installationOid);
    this.telemetryStore.setInstallation(this.installationOid);

    // Telemetry is only requested once the tab is known to be one the user may open, so a link
    // to a tab they may not open requests nothing.
    effect(() => {
      const tab = this.selectedTab();
      if (!this.permissions().length) return;
      if (!this.tabs().some(candidate => candidate.route === tab)) {
        this.onTabChange('general');
        void this.router.navigate(['/', APP_PATHS.Installations, this.installationOid, 'general'], {
          replaceUrl: true,
        });
        return;
      }
      untracked(() => this.requestTelemetryFor(tab));
    });

    effect(() => {
      const installation = this.installation();
      if (installation && !this.recorded) {
        this.recorded = true;
        this.recentlyVisited.record({
          type: 'installation',
          id: installation.installationOid,
          icon: 'electric_bolt',
          primary: installation.name,
          secondary: this.address(),
          route: ['/', APP_PATHS.Installations, installation.installationOid],
        });
      }
    });
  }

  onTabChange(tab: string): void {
    this.selectedTab.set(tab);
  }

  // Later range and date changes are fetched by the date control.
  private requestTelemetryFor(tab: string): void {
    if (tab === 'insights' && !this.telemetryRequested) {
      this.telemetryRequested = true;
      this.telemetryStore.fetch();
    }
  }
}
